import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
  useRef,
} from "react";
import { getHtsAdvertisingId } from "../utils/htsAdvertisingId";
import { usePageViewId } from "./PageViewIdProvider";

export interface HtsMediaRequestMetadata {
  channelId: string;
  pageViewId: string;
  htsAdvertisingId: string;
}

const HtsMediaRequestMetadataContext =
  createContext<HtsMediaRequestMetadata | null>(null);

/**
 * Returns the HTS Media request metadata (channelId, pageViewId, htsAdvertisingId).
 * Must be used within a HtsMediaRequestMetadataProvider.
 *
 * @throws Error if used outside of HtsMediaRequestMetadataProvider
 */
export const useHtsMediaRequestMetadata = (): HtsMediaRequestMetadata => {
  const ctx = useContext(HtsMediaRequestMetadataContext);
  if (ctx == null) {
    throw new Error(
      "useHtsMediaRequestMetadata must be used within HtsMediaRequestMetadataProvider"
    );
  }
  return ctx;
};

export interface HtsMediaRequestMetadataProviderProps
  extends PropsWithChildren {
  /**
   * The channel ID for the tenant (e.g., "smcc", "rbc").
   * Each tenant must provide their own channel ID.
   */
  channelId: string;
}

/**
 * Provides HTS Media request metadata (channelId, pageViewId, htsAdvertisingId)
 * to all descendant components via React context.
 *
 * The `channelId` must be provided as a prop by the consuming tenant.
 * The `pageViewId` is sourced from the PageViewIdProvider.
 * The `htsAdvertisingId` is a persistent device identifier from localStorage.
 */
export const HtsMediaRequestMetadataProvider = ({
  channelId,
  children,
}: HtsMediaRequestMetadataProviderProps) => {
  const pageViewId = usePageViewId();

  // Lazily initialize the advertising ID on first render rather than at
  // module scope. This avoids SSR crashes (localStorage is unavailable
  // during server-side rendering) and ensures the ID is read from
  // localStorage in the browser context after hydration.
  const htsAdvertisingIdRef = useRef<string | null>(null);
  if (htsAdvertisingIdRef.current == null) {
    htsAdvertisingIdRef.current = getHtsAdvertisingId();
  }

  const requestMetadata = useMemo(
    () => ({
      channelId,
      pageViewId,
      htsAdvertisingId: htsAdvertisingIdRef.current as string,
    }),
    [channelId, pageViewId]
  );

  return (
    <HtsMediaRequestMetadataContext.Provider value={requestMetadata}>
      {children}
    </HtsMediaRequestMetadataContext.Provider>
  );
};
